import React from 'react';
import { Headline, BodyCopy } from '@xingternal/typography';
import { SkeletonBody } from '@xingternal/skeleton';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { TopBar } from '@xingternal/top-bar';
import { Column, Row } from '@xingternal/xing-grid';

import styles from './styles.css';
import { Page } from '../../models/page';
import { LocaleMessage } from '../shared/LocaleMessage';

const getPage = (pages: Page[], slug: string): Page =>
  pages.find(page => page.slug === slug);

interface TPageLinkProps {
  slug: string;
  pages: Page[];
  pageLink: (page: Page) => string;
}
const PageLink = ({
  slug,
  pages,
  pageLink,
}: TPageLinkProps): React.ReactElement => {
  // Render skeleton when pages is not populated yet
  if (pages.length === 0) {
    return <SkeletonBody />;
  }

  const page = getPage(pages, slug);
  if (!page) {
    return null;
  }

  return (
    <li className={styles.termListItem}>
      <Link className={styles.termLink} to={pageLink(page)}>
        {page.name}
      </Link>
    </li>
  );
};

interface TSectionProps {
  slugs: string[];
  title: string;
  pages: Page[];
  pageLink: (page: Page) => string;
}
const Section = ({
  slugs,
  title,
  pages,
  pageLink,
}: TSectionProps): React.ReactElement => (
  <div className={styles.section}>
    <Headline className={styles.title} as="h3">
      <FormattedMessage id={title} />
    </Headline>

    <ul className={styles.termList}>
      {slugs.map(slug => {
        return (
          <PageLink key={slug} slug={slug} pages={pages} pageLink={pageLink} />
        );
      })}
    </ul>
  </div>
);

interface TStaticTermsProps {
  pages: Page[];
  pageLink: (page: Page) => string;
}
export const StaticTerms = ({
  pages,
  pageLink,
}: TStaticTermsProps): React.ReactElement => {
  const { formatMessage } = useIntl();

  const title = formatMessage({ id: 'LEGAL_PAGES_TERMS_AND_CONDITIONS_TITLE' });

  return (
    <div>
      <TopBar showOnLargerScreens={true} skipAdditionalButtons title={title} />

      <Row>
        <Column size={12} sizeTablet={12} sizeDesktop={8}>
          <Headline className={styles.groupTitle} size="large">
            <FormattedMessage tagName="b" id="LEGAL_PAGES_STATIC_TANDC_XING" />
          </Headline>

          <Section
            slugs={['xing', 'premium-zusatzpakete']}
            title="LEGAL_PAGES_STATIC_TANDC_B2C"
            pages={pages}
            pageLink={pageLink}
          />

          <Section
            slugs={['news-plus', 'news-seiten', 'insider']}
            title="LEGAL_PAGES_STATIC_TANDC_NEWS"
            pages={pages}
            pageLink={pageLink}
          />

          <Headline
            className={classnames(styles.groupTitle, styles.lastGroup)}
            size="large"
          >
            <FormattedMessage tagName="b" id="LEGAL_PAGES_STATIC_TANDC_B2B" />
          </Headline>

          <Section
            slugs={[
              'onlyfy-one',
              'talentmanager',
              'talentlist',
              'employer-branding-profil',
              'onlyfy-360+',
              'onlyfy-360',
              'stellenanzeigen-offline-kunden',
              'stellenanzeigen-online-kunden',
              'onlyfy-beratungs-und-serviceleistungen',
              'kununu-top-company',
            ]}
            title="LEGAL_PAGES_STATIC_TANDC_RECRUITING"
            pages={pages}
            pageLink={pageLink}
          />

          <Section
            slugs={['marketing-solutions', 'anzeigen']}
            title="LEGAL_PAGES_STATIC_TANDC_MARKETING"
            pages={pages}
            pageLink={pageLink}
          />

          <Section
            slugs={['coaches-trainer']}
            title="LEGAL_PAGES_STATIC_TANDC_COACHES"
            pages={pages}
            pageLink={pageLink}
          />

          <BodyCopy className={styles.termFooter}>
            <LocaleMessage
              tagName="span"
              id={'LEGAL_PAGES_STATIC_TANDC_FOOTER'}
              values={{
                a: (chunks: React.ReactNode) => (
                  <a
                    href="https://privacy.xing.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </BodyCopy>
        </Column>
      </Row>
    </div>
  );
};
